import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo, useEffect, useCallback } from "react";
import {
  Plus,
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Clock,
  User,
  MoreHorizontal,
  MapPin,
  Palette,
  DollarSign
} from "lucide-react";
import { useMonthSelector } from "@/hooks/useMonthSelector";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addDays,
  subDays,
  startOfDay
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { AgendamentoForm } from "@/components/AgendamentoForm";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import { useUserRole } from "@/hooks/useUserRole";

export const Route = createFileRoute("/agenda")({
  component: AgendaPage,
});


function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

function MobileAgendaList({
  agendamentos,
  onSelect,
}: {
  agendamentos: any[];
  onSelect: (a: any) => void;
}) {
  const today = startOfDay(new Date());
  const days = eachDayOfInterval({ start: today, end: addDays(today, 29) });

  const grouped = days
    .map(day => ({
      day,
      items: agendamentos
        .filter(a => a.data === format(day, "yyyy-MM-dd"))
        .sort((a, b) => a.horario_inicio.localeCompare(b.horario_inicio)),
    }))
    .filter(({ items }) => items.length > 0);

  const fmt = (val: number) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(val);

  if (grouped.length === 0) {
    return (
      <div className="bg-card border border-dashed border-border rounded-xl p-16 flex flex-col items-center justify-center text-center">
        <div className="size-16 rounded-full bg-muted flex items-center justify-center text-muted-foreground mb-4">
          <CalendarIcon size={32} />
        </div>
        <p className="text-foreground font-semibold">Nenhum agendamento nos próximos 30 dias</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {grouped.map(({ day, items }) => (
        <div key={day.toISOString()}>
          <div className="flex items-center gap-3 mb-3 px-1">
            <div className={cn(
              "size-9 rounded-xl flex items-center justify-center text-sm font-bold shrink-0",
              isSameDay(day, new Date()) ? "bg-primary text-white" : "bg-muted/60 text-foreground"
            )}>
              {format(day, "dd")}
            </div>
            <p className="text-sm font-bold text-foreground capitalize">
              {format(day, "EEEE, dd 'de' MMMM", { locale: ptBR })}
            </p>
          </div>
          <div className="space-y-2 pl-12">
            {items.map(a => (
              <button
                key={a.id}
                onClick={() => onSelect(a)}
                className={cn(
                  "w-full text-left bg-card border border-border rounded-xl p-4 border-l-4 transition-all active:scale-[0.98]",
                  a.tipo === "cliente" ? "border-l-primary" : "border-l-zinc-500"
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-bold text-foreground leading-none mb-1">
                      {a.horario_inicio?.slice(0, 5)}
                    </p>
                    <p className="font-semibold text-foreground truncate">
                      {a.cliente_nome || a.titulo}
                    </p>
                    {a.tipo === "cliente" && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {a.estilo} • {a.regiao_corpo}
                      </p>
                    )}
                  </div>
                  {a.tipo === "cliente" && (
                    <p className="text-primary font-semibold text-sm shrink-0">
                      {fmt(a.valor_total || 0)}
                    </p>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function AgendaPage() {
  const { selectedDate: selectorDate, setSelectedDate: setSelectorDate, MonthSelector } = useMonthSelector();
  const isMobile = useIsMobile();
  const [currentDate, setCurrentDate] = useState(() => startOfDay(new Date()));
  const [view, setView] = useState("mes");
  const [agendamentos, setAgendamentos] = useState<any[]>([]);
  const [clientes, setClientes] = useState<any[]>([]);
  const [equipe, setEquipe] = useState<any[]>([]);
  const [selectedTatuador, setSelectedTatuador] = useState<string>("todos");
  const { loading: roleLoading, isDono: isDonoRole, userId: currentUserId, nome: currentUserNome, effectiveStudioId } = useUserRole();
  const isDono = roleLoading ? null : isDonoRole;
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [novoAgendamentoData, setNovoAgendamentoData] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingAgendamento, setEditingAgendamento] = useState<any>(null);
  const [selectedAgendamento, setSelectedAgendamento] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Sincronizar currentDate com selectorDate apenas para view de mês
  useEffect(() => {
    if (view === "mes") {
      setCurrentDate(startOfMonth(selectorDate));
    }
  }, [selectorDate, view]);

  const fetchAgendamentos = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const today = startOfDay(new Date());
      // Mobile: sempre próximos 30 dias; Semana: intervalo da semana; Mês/Dia: mês inteiro
      const start = isMobile
        ? format(today, "yyyy-MM-dd")
        : view === "semana"
          ? format(startOfWeek(currentDate, { weekStartsOn: 0 }), "yyyy-MM-dd")
          : format(startOfMonth(currentDate), "yyyy-MM-01");
      const end = isMobile
        ? format(addDays(today, 30), "yyyy-MM-dd")
        : view === "semana"
          ? format(endOfWeek(currentDate, { weekStartsOn: 0 }), "yyyy-MM-dd")
          : format(endOfMonth(currentDate), "yyyy-MM-dd");

      let query = supabase
        .from("agendamentos")
        .select("*")
        .gte("data", start)
        .lte("data", end);

      if (!isDono) {
        // Tatuador vê apenas seus próprios agendamentos
        query = query.eq("tatuador_id", user.id);
      } else {
        const teamIds = equipe.map((e) => e.tatuador_id).filter(Boolean);
        if (selectedTatuador !== "todos") {
          // Um tatuador específico (o próprio dono ou um membro da equipe).
          // A RLS cobre ambos: studio_all (dono) e dono_equipe_select (membros).
          query = query.eq("tatuador_id", selectedTatuador);
        } else if (teamIds.length > 0) {
          // "Todos": agendamentos do studio do dono + de todos os membros da equipe
          query = query.or(`studio_id.eq.${user.id},tatuador_id.in.(${teamIds.join(",")})`);
        } else {
          query = query.eq("studio_id", user.id);
        }
      }

      const { data, error } = await query;
      if (error) throw error;
      setAgendamentos(data || []);
    } catch (error: any) {
      console.error("Erro ao buscar agendamentos:", error);
      toast.error("Erro ao carregar agenda");
    } finally {
      setIsLoading(false);
    }
  }, [currentDate, isDono, selectedTatuador, isMobile, view, equipe]);

  const fetchEquipe = useCallback(async () => {
    if (!effectiveStudioId) return;
    try {
      const { data, error } = await supabase
        .from("equipe")
        .select("*, usuarios:tatuador_id(nome)")
        .eq("studio_id", effectiveStudioId);
      if (error) throw error;
      setEquipe(data || []);
    } catch (error) {
      console.error("Erro ao buscar equipe:", error);
    }
  }, [effectiveStudioId]);

  const fetchClientes = useCallback(async () => {
    if (!effectiveStudioId) return;
    try {
      const { data, error } = await supabase
        .from("clientes")
        .select("id, nome")
        .eq("studio_id", effectiveStudioId);
      if (error) throw error;
      setClientes(data || []);
    } catch (error) {
      console.error("Erro ao buscar clientes:", error);
    }
  }, [effectiveStudioId]);

  useEffect(() => {
    if (roleLoading) return;
    fetchClientes();
    fetchEquipe();
  }, [roleLoading, fetchClientes, fetchEquipe]);

  useEffect(() => {
    if (isDono !== null) fetchAgendamentos();
  }, [fetchAgendamentos, isDono]);

  useRealtimeSubscription("agendamentos", fetchAgendamentos);
  useRealtimeSubscription("equipe", fetchEquipe);

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(monthStart);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 0 });
  const endDate = endOfWeek(monthEnd, { weekStartsOn: 0 });

  const calendarDays = eachDayOfInterval({ start: startDate, end: endDate });

  const nextPeriod = () => {
    if (view === "mes") {
      const next = addMonths(currentDate, 1);
      setCurrentDate(next);
      setSelectorDate(startOfMonth(next));
    }
    if (view === "semana") setCurrentDate(addDays(currentDate, 7));
    if (view === "dia") setCurrentDate(addDays(currentDate, 1));
  };

  const prevPeriod = () => {
    if (view === "mes") {
      const prev = subMonths(currentDate, 1);
      setCurrentDate(prev);
      setSelectorDate(startOfMonth(prev));
    }
    if (view === "semana") setCurrentDate(subDays(currentDate, 7));
    if (view === "dia") setCurrentDate(subDays(currentDate, 1));
  };

  const recalcularTotalGasto = async (clienteId: string) => {
    const { data: ags } = await supabase
      .from("agendamentos")
      .select("valor_total")
      .eq("cliente_id", clienteId)
      .neq("status", "Cancelado");
    const total = (ags || []).reduce((acc, a) => acc + (parseFloat(a.valor_total) || 0), 0);
    await supabase.from("clientes").update({ total_gasto: total }).eq("id", clienteId);
  };

  // Gera/atualiza o lançamento de ENTRADA referente ao SINAL pago do agendamento.
  // - Agendamento ativo: o sinal é uma entrada "gerada" (geradoAutomaticamente),
  //   não somada de novo no faturamento (o faturamento vem do próprio agendamento);
  //   o restante (total - sinal) fica como "a receber" em "Entradas Futuras".
  // - Agendamento cancelado: o sinal é RETIDO (não devolvido). Vira uma entrada
  //   definitiva (geradoAutomaticamente = false) que passa a contar no faturamento.
  //   O valor restante deixa de existir junto com o agendamento.
  const sincronizarSinalFinanceiro = async (
    userId: string,
    ag: any,
    anterior?: { data?: string; cliente_nome?: string }
  ) => {
    const alvoData = anterior?.data || ag.data;
    const alvoNome = anterior?.cliente_nome || ag.cliente_nome;
    const cancelado = ag.status === "Cancelado";

    // Remove o lançamento de sinal AINDA GERADO deste agendamento.
    // Uma entrada de sinal já retida (geradoAutomaticamente = false) nunca é tocada.
    if (alvoData && alvoNome) {
      await supabase
        .from("financeiro")
        .delete()
        .eq("studio_id", userId)
        .eq("geradoAutomaticamente", true)
        .eq("categoria", "Sinal de Tatuagem")
        .eq("data", alvoData)
        .ilike("descricao", `Sinal - ${alvoNome}%`);
    }

    if (ag.tipo !== "cliente") return;

    const valorSinal = parseFloat(ag.valor_sinal) || 0;
    if (valorSinal <= 0) return;

    // Evita duplicar a entrada retida ao editar de novo um agendamento cancelado
    if (cancelado && ag.cliente_nome) {
      const { data: jaRetido } = await supabase
        .from("financeiro")
        .select("id")
        .eq("studio_id", userId)
        .eq("categoria", "Sinal de Tatuagem")
        .eq("geradoAutomaticamente", false)
        .ilike("descricao", `Sinal retido%${ag.cliente_nome}%`)
        .limit(1);
      if (jaRetido && jaRetido.length > 0) return;
    }

    const { error } = await supabase.from("financeiro").insert({
      studio_id: userId,
      tatuador_id: ag.tatuador_id || currentUserId || null,
      tipo: "entrada",
      categoria: "Sinal de Tatuagem",
      descricao: cancelado
        ? `Sinal retido (agend. cancelado) - ${ag.cliente_nome}`
        : `Sinal - ${ag.cliente_nome}`,
      valor: valorSinal,
      data: ag.data,
      status: "confirmado",
      geradoAutomaticamente: !cancelado,
    });
    if (error) console.error("Erro ao gerar entrada do sinal:", error);
  };

  const handleUpdate = async (data: any) => {
    if (!editingAgendamento) return;
    try {
      if (!effectiveStudioId) throw new Error("Usuário não autenticado");

      const agComTatuador = { ...data, tatuador_id: data.tatuador_id || currentUserId || null };

      const { error } = await supabase
        .from("agendamentos")
        .update(agComTatuador)
        .eq("id", editingAgendamento.id);

      if (error) throw error;

      if (data.tipo === "cliente" || editingAgendamento.tipo === "cliente") {
        await sincronizarSinalFinanceiro(effectiveStudioId, agComTatuador, {
          data: editingAgendamento.data,
          cliente_nome: editingAgendamento.cliente_nome,
        });
      }

      if (data.tipo === "cliente" && data.cliente_id) {
        await recalcularTotalGasto(data.cliente_id);
      }

      toast.success("Agendamento atualizado!");
      setIsEditModalOpen(false);
      setEditingAgendamento(null);
      fetchAgendamentos();
    } catch (error: any) {
      toast.error("Erro ao atualizar: " + error.message);
    }
  };

  const handleCancelAgendamento = async () => {
    if (!selectedAgendamento) return;
    try {
      if (!effectiveStudioId) throw new Error("Usuário não autenticado");

      const { error } = await supabase
        .from("agendamentos")
        .delete()
        .eq("id", selectedAgendamento.id);

      if (error) throw error;

      if (selectedAgendamento.tipo === "cliente") {
        // O sinal é retido (não é devolvido ao cliente): o lançamento gerado
        // vira uma entrada definitiva e passa a contar no faturamento do mês.
        // O valor restante (total - sinal) deixa de existir junto com o agendamento.
        await supabase
          .from("financeiro")
          .update({
            geradoAutomaticamente: false,
            descricao: `Sinal retido (agend. cancelado) - ${selectedAgendamento.cliente_nome}`,
          })
          .eq("studio_id", effectiveStudioId)
          .eq("geradoAutomaticamente", true)
          .eq("categoria", "Sinal de Tatuagem")
          .eq("data", selectedAgendamento.data)
          .ilike("descricao", `Sinal - ${selectedAgendamento.cliente_nome}%`);

        if (selectedAgendamento.cliente_id) {
          await recalcularTotalGasto(selectedAgendamento.cliente_id);
        }
      }

      toast.success("Agendamento removido!");
      setSelectedAgendamento(null);
      fetchAgendamentos();
    } catch (error: any) {
      toast.error("Erro ao cancelar: " + error.message);
    }
  };

  const handleSave = async (data: any) => {
    try {
      if (!effectiveStudioId) throw new Error("Usuário não autenticado");

      const agComTatuador = { ...data, tatuador_id: data.tatuador_id || currentUserId || null };

      const { error } = await supabase
        .from("agendamentos")
        .insert({ ...agComTatuador, studio_id: effectiveStudioId });

      if (error) throw error;

      if (data.tipo === "cliente") {
        await sincronizarSinalFinanceiro(effectiveStudioId, agComTatuador);
      }

      if (data.tipo === "cliente" && data.cliente_id) {
        await recalcularTotalGasto(data.cliente_id);
      }

      toast.success("Agendamento salvo com sucesso!");
      setIsDialogOpen(false);
      fetchAgendamentos();
    } catch (error: any) {
      console.error("Erro ao salvar agendamento:", error);
      toast.error("Erro ao salvar agendamento: " + error.message);
    }
  };

  const getAgendamentosDay = (day: Date) =>
    agendamentos.filter(a => a.data === format(day, "yyyy-MM-dd"));

  const getTatuadorNome = (tatuador_id: string) => {
    if (tatuador_id === currentUserId) return currentUserNome || "Dono";
    const membro = equipe.find(e => e.tatuador_id === tatuador_id);
    return membro?.usuarios?.nome || tatuador_id.slice(0, 4);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-20 md:pb-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div>
            <h1 className="text-3xl font-semibold text-foreground">Agenda</h1>
            <p className="text-muted-foreground">Gerencie seus horários e tatuagens</p>
          </div>
          {/* Seletor de mês visível no desktop para mês e semana */}
          {(view === "mes" || view === "semana") && !isMobile && <MonthSelector />}
        </div>

        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) setNovoAgendamentoData(null); }}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90 text-white font-semibold h-11 w-full md:w-auto" onClick={() => setNovoAgendamentoData(null)}>
              <Plus className="mr-2 size-4" />
              Novo Agendamento
            </Button>
          </DialogTrigger>
          <DialogContent className="p-0 border-none bg-transparent max-w-2xl shadow-none">
            <AgendamentoForm
              title="Novo Agendamento"
              initialData={novoAgendamentoData ? { data: novoAgendamentoData } : undefined}
              clientes={clientes}
              equipe={equipe}
              isDono={isDono ?? true}
              currentUserId={currentUserId}
              currentUserNome={currentUserNome}
              effectiveStudioId={effectiveStudioId}
              onSubmit={handleSave}
              onCancel={() => setIsDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="mes" value={view} onValueChange={(v) => { if (v === "semana") setCurrentDate(startOfDay(new Date())); setView(v); }} className="w-full">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <TabsList className="bg-card border border-border h-11 p-1">
              <TabsTrigger value="mes" className="data-[state=active]:bg-primary data-[state=active]:text-white font-semibold px-6">Mês</TabsTrigger>
              <TabsTrigger value="semana" className="data-[state=active]:bg-primary data-[state=active]:text-white font-semibold px-6">Semana</TabsTrigger>
              <TabsTrigger value="dia" className="data-[state=active]:bg-primary data-[state=active]:text-white font-semibold px-6">Dia</TabsTrigger>
            </TabsList>

            {isDono && (
              <Select value={selectedTatuador} onValueChange={setSelectedTatuador}>
                <SelectTrigger className="w-[200px] h-11 bg-card border-border">
                  <SelectValue placeholder="Ver Tatuador" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border text-foreground">
                  <SelectItem value="todos">Todos os Tatuadores</SelectItem>
                  {equipe.map(t => (
                    <SelectItem key={t.id} value={t.tatuador_id}>{t.usuarios?.nome || "Tatuador"}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Nav de período: oculta na semana mobile (lista não usa navegação de semana) */}
          {view !== "mes" && !(isMobile && view === "semana") && (
            <div className="flex items-center gap-4 bg-card border border-border p-1 rounded-lg shadow-sm">
              <Button variant="ghost" size="icon" onClick={prevPeriod} className="text-foreground hover:bg-white/5 h-9 w-9"><ChevronLeft size={20} /></Button>
              <span className="text-foreground font-semibold min-w-[150px] text-center capitalize">
                {view === "dia"
                  ? format(currentDate, "dd 'de' MMMM", { locale: ptBR })
                  : `Semana de ${format(startOfWeek(currentDate, { weekStartsOn: 0 }), "dd/MM")}`}
              </span>
              <Button variant="ghost" size="icon" onClick={nextPeriod} className="text-foreground hover:bg-white/5 h-9 w-9"><ChevronRight size={20} /></Button>
            </div>
          )}
        </div>

        {/* ── MÊS ── */}
        <TabsContent value="mes" className="mt-0">
          {isMobile ? (
            <MobileAgendaList agendamentos={agendamentos} onSelect={setSelectedAgendamento} />
          ) : (
            <div className="grid grid-cols-7 border-t border-l border-border rounded-t-xl overflow-hidden shadow-2xl">
              {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"].map(day => (
                <div key={day} className="bg-card border-r border-b border-border p-4 text-center text-xs font-medium text-primary uppercase tracking-[0.2em]">
                  {day}
                </div>
              ))}
              {calendarDays.map((day, idx) => {
                const dayAgendamentos = getAgendamentosDay(day);
                const isToday = isSameDay(day, new Date());
                const isCurrentMonth = isSameMonth(day, monthStart);
                const isWeekend = day.getDay() === 0 || day.getDay() === 6;

                return (
                  <div
                    key={idx}
                    onClick={() => { setNovoAgendamentoData(format(day, "yyyy-MM-dd")); setIsDialogOpen(true); }}
                    className={cn(
                      "min-h-[120px] md:min-h-[90px] border-r border-b border-border p-2 md:p-3 cursor-pointer transition-all duration-200 hover:bg-white/[0.03] group relative",
                      isWeekend ? "bg-background" : "bg-card",
                      !isCurrentMonth && "opacity-20",
                      isToday && "ring-2 ring-inset ring-primary z-10"
                    )}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span
                        onClick={(e) => { e.stopPropagation(); setCurrentDate(day); setView("dia"); }}
                        className={cn(
                          "text-base font-semibold transition-colors",
                          isToday ? "text-primary" : "text-foreground group-hover:text-primary"
                        )}>
                        {format(day, "d")}
                      </span>
                    </div>
                    <div className="space-y-1">
                      {dayAgendamentos.slice(0, 4).map(a => (
                        <div
                          key={a.id}
                          onClick={(e) => { e.stopPropagation(); setCurrentDate(day); setView("dia"); }}
                          className={cn(
                            "text-[11px] p-2 rounded-r border-l-[4px] flex flex-col justify-center min-h-[38px] leading-tight",
                            a.tipo === "cliente"
                              ? "bg-white border-[#DC2626] text-[#111111] shadow-sm"
                              : "bg-zinc-100 border-zinc-400 text-zinc-700"
                          )}
                        >
                          <span className="font-bold shrink-0">{a.horario_inicio?.slice(0, 5)}</span>
                          <span className="truncate font-medium">{a.cliente_nome || a.titulo}</span>
                          {isDono && selectedTatuador === "todos" && a.tatuador_id && (
                            <Badge className="text-[7px] p-0.5 mt-1 bg-blue-500/10 text-blue-500 border-none uppercase">
                              {getTatuadorNome(a.tatuador_id)}
                            </Badge>
                          )}
                        </div>
                      ))}
                      {dayAgendamentos.length > 4 && (
                        <div className="text-[10px] text-muted-foreground font-semibold pl-2 py-0.5">
                          + {dayAgendamentos.length - 4} itens
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ── SEMANA ── */}
        <TabsContent value="semana" className="mt-0">
          {isMobile ? (
            <MobileAgendaList agendamentos={agendamentos} onSelect={setSelectedAgendamento} />
          ) : (
            <>
              <div className="bg-card border border-border rounded-xl overflow-x-auto custom-scrollbar">
                <div className="min-w-[800px]">
                  <div className="flex border-b border-border bg-muted/30">
                    <div className="w-32 shrink-0 p-4 border-r border-border font-bold text-xs uppercase text-muted-foreground flex items-center justify-center">
                      Horários
                    </div>
                    <div className="flex flex-1">
                      {Array.from({ length: 15 }).map((_, i) => {
                        const hour = (i + 8).toString().padStart(2, "0") + ":00";
                        return (
                          <div key={hour} className="flex-1 min-w-[80px] p-4 text-center border-r border-border last:border-0 font-bold text-xs text-foreground">
                            {hour}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="flex flex-col">
                    {eachDayOfInterval({
                      start: startOfWeek(currentDate, { weekStartsOn: 0 }),
                      end: endOfWeek(currentDate, { weekStartsOn: 0 }),
                    }).map(day => (
                      <div key={day.toString()} className="flex border-b border-border last:border-0 min-h-[100px] group">
                        <div className="w-32 shrink-0 p-4 border-r border-border bg-muted/10 flex flex-col items-center justify-center">
                          <p className="text-[10px] font-bold text-muted-foreground uppercase mb-1">{format(day, "eee", { locale: ptBR })}</p>
                          <p className={cn(
                            "text-xl font-bold",
                            isSameDay(day, new Date()) ? "text-[#DC2626]" : "text-foreground"
                          )}>{format(day, "dd/MM")}</p>
                        </div>
                        <div className="flex flex-1 relative">
                          {Array.from({ length: 15 }).map((_, i) => {
                            const hourStr = (i + 8).toString().padStart(2, "0");
                            const dayAgendamentos = getAgendamentosDay(day).filter(a => a.horario_inicio.startsWith(hourStr));

                            return (
                              <div key={i} className="flex-1 min-w-[80px] border-r border-border/30 last:border-0 relative min-h-[100px] group-hover:bg-muted/5">
                                {dayAgendamentos.map(a => {
                                  const duration = parseFloat(String(a.duracao)) || 1;
                                  return (
                                    <div
                                      key={a.id}
                                      onClick={() => setSelectedAgendamento(a)}
                                      className={cn(
                                        "absolute top-0 bottom-0 left-0 rounded-r border-l-[4px] p-2 shadow-md cursor-pointer transition-transform hover:scale-[1.01] flex flex-col justify-center overflow-hidden",
                                        a.tipo === "cliente"
                                          ? "bg-white border-[#DC2626] text-[#111111]"
                                          : "bg-zinc-100 border-zinc-400 text-zinc-700"
                                      )}
                                      style={{ width: `calc(${duration * 100}% - 2px)`, zIndex: 20 }}
                                    >
                                      <p className="text-sm font-bold leading-none mb-1">{a.horario_inicio?.slice(0, 5)}</p>
                                      <p className="text-sm font-semibold truncate leading-tight">{a.cliente_nome || a.titulo}</p>
                                      {a.tipo === "cliente" && <p className="text-xs opacity-70 truncate mt-0.5">{a.regiao_corpo}</p>}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-muted-foreground text-xs mt-4 text-center">Clique em um agendamento para ver os detalhes</p>
            </>
          )}
        </TabsContent>

        {/* ── DIA (igual em todos os dispositivos) ── */}
        <TabsContent value="dia" className="mt-0 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-3 space-y-4">
              {getAgendamentosDay(currentDate).length > 0 ? (
                getAgendamentosDay(currentDate)
                  .sort((a, b) => a.horario_inicio.localeCompare(b.horario_inicio))
                  .map(a => (
                    <Card
                      key={a.id}
                      className={cn(
                        "bg-card border-border hover:border-white/10 transition-all cursor-pointer overflow-hidden",
                        a.tipo === "cliente" ? "border-l-4 border-l-primary" : "border-l-4 border-l-zinc-500"
                      )}
                      onClick={() => setSelectedAgendamento(a)}
                    >
                      <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex items-center gap-6">
                            <div className="flex flex-col items-center justify-center bg-background p-3 rounded-xl min-w-[80px] border border-border">
                              <Clock size={20} className="text-primary mb-1" />
                              <span className="text-foreground font-semibold text-lg">{a.horario_inicio?.slice(0, 5)}</span>
                            </div>
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <h3 className="text-xl font-semibold text-foreground">{a.cliente_nome || a.titulo}</h3>
                                {a.tipo === "cliente" && (
                                  <Badge className="bg-primary/10 text-primary border-primary/20 font-medium uppercase text-[10px]">
                                    Tatuagem
                                  </Badge>
                                )}
                              </div>
                              {a.tipo === "cliente" && (
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                                  <span className="flex items-center gap-1"><MapPin size={14} /> {a.regiao_corpo}</span>
                                  <span className="flex items-center gap-1"><Palette size={14} /> {a.estilo}</span>
                                  <span className="flex items-center gap-1"><Clock size={14} /> {a.duracao}h de sessão</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {a.tipo === "cliente" && (
                            <div className="flex items-center gap-6 md:text-right border-t md:border-t-0 border-border pt-4 md:pt-0">
                              <div className="space-y-1">
                                <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-widest">Valor Total</p>
                                <p className="text-xl font-semibold text-primary">
                                  {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(a.valor_total || 0)}
                                </p>
                              </div>
                              <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground"><MoreHorizontal /></Button>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))
              ) : (
                <div className="bg-card border border-dashed border-border rounded-xl p-20 flex flex-col items-center justify-center text-center">
                  <div className="size-16 rounded-full bg-muted flex items-center justify-center text-muted-foreground mb-4">
                    <CalendarIcon size={32} />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">Sem agendamentos para hoje</h3>
                  <p className="text-muted-foreground max-w-xs mx-auto">Tudo tranquilo por aqui. Aproveite para organizar seus materiais ou descansar!</p>
                  <Button className="mt-6 bg-primary hover:bg-primary/90 text-white font-semibold" onClick={() => setIsDialogOpen(true)}>
                    Agendar agora
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-6">
              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-widest mb-4">Resumo do Dia</h4>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Agendamentos</span>
                      <span className="text-foreground font-semibold">{getAgendamentosDay(currentDate).filter(a => a.tipo === "cliente").length}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Compromissos</span>
                      <span className="text-foreground font-semibold">{getAgendamentosDay(currentDate).filter(a => a.tipo === "pessoal").length}</span>
                    </div>
                    <div className="pt-4 border-t border-border flex justify-between items-center">
                      <span className="text-muted-foreground font-semibold">Total Estimado</span>
                      <span className="text-primary font-semibold text-xl">
                        {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
                          getAgendamentosDay(currentDate).reduce((acc, a) => acc + (a.valor_total || 0), 0)
                        )}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Modal de Detalhes */}
      <Dialog open={!!selectedAgendamento} onOpenChange={(open) => !open && setSelectedAgendamento(null)}>
        <DialogContent className="p-0 border-none bg-transparent max-w-2xl shadow-none">
          {selectedAgendamento && (
            <div className="bg-card text-foreground p-8 rounded-2xl border border-border w-full max-w-2xl mx-auto shadow-2xl animate-in zoom-in-95 duration-200">
              <div className="flex justify-between items-start mb-8">
                <div>
                  <Badge className={cn(
                    "mb-2 font-medium uppercase text-[10px]",
                    selectedAgendamento.tipo === "cliente" ? "bg-primary text-foreground" : "bg-zinc-500 text-white"
                  )}>
                    {selectedAgendamento.tipo === "cliente" ? "Tatuagem" : "Compromisso Pessoal"}
                  </Badge>
                  <h2 className="text-3xl font-semibold">{selectedAgendamento.cliente_nome || selectedAgendamento.titulo}</h2>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setSelectedAgendamento(null)} className="text-muted-foreground hover:text-foreground hover:bg-white/5">
                  <X size={24} />
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="size-12 rounded-xl bg-background border border-border flex items-center justify-center text-primary">
                      <CalendarIcon size={24} />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-widest">Data</p>
                      <p className="text-lg font-semibold text-foreground">
                        {selectedAgendamento.data ? format(new Date((selectedAgendamento.data as string) + "T12:00:00"), "dd 'de' MMMM, yyyy", { locale: ptBR }) : ""}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="size-12 rounded-xl bg-background border border-border flex items-center justify-center text-primary">
                      <Clock size={24} />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-widest">Horário</p>
                      <p className="text-lg font-semibold text-foreground">
                        {selectedAgendamento.horario_inicio?.slice(0, 5)}
                        {selectedAgendamento.duracao && ` (${selectedAgendamento.duracao}h de sessão)`}
                        {selectedAgendamento.horario_fim && ` até ${selectedAgendamento.horario_fim.slice(0, 5)}`}
                      </p>
                    </div>
                  </div>

                  {selectedAgendamento.tipo === "cliente" && (
                    <div className="flex items-center gap-4">
                      <div className="size-12 rounded-xl bg-background border border-border flex items-center justify-center text-primary">
                        <MapPin size={24} />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground font-medium uppercase tracking-widest">Local & Estilo</p>
                        <p className="text-lg font-semibold text-foreground">{selectedAgendamento.regiao_corpo} • {selectedAgendamento.estilo}</p>
                      </div>
                    </div>
                  )}
                </div>

                {selectedAgendamento.tipo === "cliente" && (
                  <div className="bg-background border border-border rounded-2xl p-6 space-y-6">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-xs text-muted-foreground font-medium uppercase tracking-widest mb-1">Valor Total</p>
                        <p className="text-2xl font-semibold text-primary">
                          {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(selectedAgendamento.valor_total)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground font-medium uppercase tracking-widest mb-1">Sinal Pago</p>
                        <p className="text-lg font-semibold text-green-500">
                          {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(selectedAgendamento.valor_sinal)}
                        </p>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-border">
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-widest mb-1">Restante a pagar</p>
                      <p className="text-xl font-semibold text-foreground">
                        {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
                          (selectedAgendamento.valor_total || 0) - (selectedAgendamento.valor_sinal || 0)
                        )}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {selectedAgendamento.observacoes && (
                <div className="mb-8">
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-widest mb-2">Observações</p>
                  <p className="bg-background border border-border rounded-xl p-4 text-zinc-300 italic">
                    "{selectedAgendamento.observacoes}"
                  </p>
                </div>
              )}

              <div className="flex gap-4">
                <Button
                  className="flex-1 bg-white/5 hover:bg-white/10 text-foreground font-semibold h-12 border border-border"
                  onClick={() => {
                    setEditingAgendamento(selectedAgendamento);
                    setSelectedAgendamento(null);
                    setIsEditModalOpen(true);
                  }}
                >
                  Editar Agendamento
                </Button>
                <Button
                  className="flex-1 bg-transparent hover:bg-red-500/10 text-red-500 font-semibold h-12 border border-red-500/20"
                  onClick={handleCancelAgendamento}
                >
                  Cancelar Agendamento
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Modal de Edição */}
      <Dialog open={isEditModalOpen} onOpenChange={(open) => { if (!open) { setIsEditModalOpen(false); setEditingAgendamento(null); } }}>
        <DialogContent className="p-0 border-none bg-transparent max-w-2xl shadow-none">
          {editingAgendamento && (
            <AgendamentoForm
              title="Editar Agendamento"
              clientes={clientes}
              equipe={equipe}
              isDono={isDono ?? true}
              currentUserId={currentUserId}
              currentUserNome={currentUserNome}
              effectiveStudioId={effectiveStudioId}
              initialData={editingAgendamento}
              onSubmit={handleUpdate}
              onCancel={() => { setIsEditModalOpen(false); setEditingAgendamento(null); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const X = ({ size, className }: { size?: number; className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size || 24}
    height={size || 24}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M18 6 6 18" /><path d="m6 6 12 12" />
  </svg>
);
